import { useMemo } from 'react';
import { MapPin } from 'lucide-react';
import type { CalendarEvent, Member, EventDensity, EquipmentFlag } from '../../types/calendar';
import { MemberDotsStack } from './MemberDotsStack';
import { formatEventDuration, getDescriptionSnippet, MIN_EVENT_HEIGHTS } from '../../lib/event-density';

interface EventCardProps {
  event: CalendarEvent;
  member?: Member | null;
  /** Multiple members for shared events - shows stacked color dots */
  members?: Member[];
  density: EventDensity;
  formatTime: (date: Date | string) => string;
  onClick?: () => void;
  style?: React.CSSProperties;
  className?: string;
  /** Whether this event is in the past - applies faded styling */
  isPast?: boolean;
}

/**
 * Adaptive event card that displays different detail levels based on density.
 *
 * Used by:
 * - Home.tsx (3-day family view)
 * - Calendar.tsx (Day/Week views)
 *
 * Density levels:
 * - spacious: Full details (title, time, duration, member name, location, description)
 * - normal: Standard display (title, time, member color)
 * - compact: Minimal display (time + truncated title inline)
 */
export function EventCard({
  event,
  member,
  members,
  density,
  formatTime,
  onClick,
  style,
  className = '',
  isPast = false,
}: EventCardProps) {
  const duration = useMemo(() => formatEventDuration(event), [event]);
  const descriptionSnippet = useMemo(
    () => (density === 'spacious' ? getDescriptionSnippet(event.description) : null),
    [event.description, density]
  );

  const hasEquipmentFlags = event.equipmentFlags && event.equipmentFlags.length > 0;

  // Get the primary member color for equipment-flagged background tint
  const memberColor = useMemo(() => {
    if (!hasEquipmentFlags) return null;
    const memberList = members && members.length > 0 ? members : member ? [member] : [];
    return memberList[0]?.color ?? null;
  }, [hasEquipmentFlags, members, member]);

  const baseClasses =
    'rounded-lg px-2 py-1 overflow-hidden cursor-pointer bg-bg-surface ' +
    'shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] border border-gray-100 ' +
    'hover:shadow-md transition-shadow';

  // Calculate minimum height based on density
  const minHeight = MIN_EVENT_HEIGHTS[density];

  // Generate border style - gradient for multiple members, solid for single
  const borderStyle = useMemo(() => {
    const memberList = members && members.length > 0 ? members : member ? [member] : [];
    if (memberList.length <= 1) {
      const color = memberList[0]?.color ?? 'var(--color-soft-cyan)';
      // For past events, desaturate the color
      const displayColor = isPast
        ? `color-mix(in srgb, ${color} 60%, #9ca3af 40%)`
        : color;
      return { borderLeftColor: displayColor };
    }
    // Equal vertical segments for each member color
    // For past events, desaturate each color in the gradient
    const stops = memberList
      .map((m, i) => {
        const start = (i / memberList.length) * 100;
        const end = ((i + 1) / memberList.length) * 100;
        const color = isPast
          ? `color-mix(in srgb, ${m.color} 60%, #9ca3af 40%)`
          : m.color;
        return `${color} ${start}% ${end}%`;
      })
      .join(', ');
    return {
      borderLeftColor: 'transparent',
      borderImage: `linear-gradient(to bottom, ${stops}) 1`,
    };
  }, [members, member, isPast]);

  // Show member dots when multiple members share this event
  const showMemberDots = members && members.length > 1;

  // Build CSS classes including past state
  const cardClasses = `${baseClasses} ${className} ${isPast ? 'event-card--past' : ''}`;

  return (
    <div
      className={cardClasses}
      style={{
        ...style,
        minHeight,
        borderLeftWidth: '4px',
        ...borderStyle,
        backgroundColor: hasEquipmentFlags && memberColor
          ? `color-mix(in srgb, ${memberColor} 15%, var(--bg-surface, white) 85%)`
          : undefined,
      }}
      title={`${event.title}${event.location ? ` - ${event.location}` : ''}${isPast ? ' (past)' : ''}`}
      aria-label={`${event.title}${isPast ? ', past event' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      {density === 'compact' && (
        <CompactContent
          event={event}
          formatTime={formatTime}
          members={members}
          showMemberDots={showMemberDots}
          hasEquipmentFlags={hasEquipmentFlags}
        />
      )}

      {density === 'normal' && (
        <NormalContent
          event={event}
          formatTime={formatTime}
          members={members}
          showMemberDots={showMemberDots}
          hasEquipmentFlags={hasEquipmentFlags}
        />
      )}

      {density === 'spacious' && (
        <SpaciousContent
          event={event}
          formatTime={formatTime}
          member={member}
          members={members}
          showMemberDots={showMemberDots}
          duration={duration}
          descriptionSnippet={descriptionSnippet}
          hasEquipmentFlags={hasEquipmentFlags}
          equipmentFlags={event.equipmentFlags}
        />
      )}
    </div>
  );
}

/**
 * Compact: time + truncated title inline (for high-density views)
 */
function CompactContent({
  event,
  formatTime,
  members,
  showMemberDots,
  hasEquipmentFlags,
}: {
  event: CalendarEvent;
  formatTime: (date: Date | string) => string;
  members?: Member[];
  showMemberDots?: boolean;
  hasEquipmentFlags?: boolean;
}) {
  return (
    <div className="flex items-center gap-1 text-xs">
      {showMemberDots && members && (
        <MemberDotsStack members={members} maxVisible={2} size="sm" />
      )}
      {hasEquipmentFlags && <span className="shrink-0" aria-label="Equipment needed">🎒</span>}
      <span className="font-medium text-text-primary line-clamp-1 lg:line-clamp-2 break-words">{event.title}</span>
      <span className="text-text-muted shrink-0 ml-auto">{formatTime(event.startTime)}</span>
    </div>
  );
}

/**
 * Normal: title + time on two lines (standard display)
 */
function NormalContent({
  event,
  formatTime,
  members,
  showMemberDots,
  hasEquipmentFlags,
}: {
  event: CalendarEvent;
  formatTime: (date: Date | string) => string;
  members?: Member[];
  showMemberDots?: boolean;
  hasEquipmentFlags?: boolean;
}) {
  return (
    <>
      <div className="flex items-center gap-1">
        {showMemberDots && members && (
          <MemberDotsStack members={members} maxVisible={2} size="sm" />
        )}
        {hasEquipmentFlags && <span className="shrink-0" aria-label="Equipment needed">🎒</span>}
        <span className="text-xs lg:text-sm font-medium text-text-primary line-clamp-2 break-words">{event.title}</span>
      </div>
      <div className="text-xs lg:text-sm text-text-secondary">{formatTime(event.startTime)}</div>
    </>
  );
}

/**
 * Spacious: full details with location, duration, member name, description snippet
 */
/** Map equipment categories to display emoji */
function getCategoryEmoji(category: string | null): string {
  switch (category?.toLowerCase()) {
    case 'sport': return '🏃';
    case 'music': return '🎵';
    case 'art': return '🎨';
    case 'swimming': return '🏊';
    case 'dance': return '💃';
    case 'tech': return '💻';
    default: return '📦';
  }
}

function SpaciousContent({
  event,
  formatTime,
  member,
  members,
  showMemberDots,
  duration,
  descriptionSnippet,
  hasEquipmentFlags,
  equipmentFlags,
}: {
  event: CalendarEvent;
  formatTime: (date: Date | string) => string;
  member?: Member | null;
  members?: Member[];
  showMemberDots?: boolean;
  duration: string;
  descriptionSnippet: string | null;
  hasEquipmentFlags?: boolean;
  equipmentFlags?: EquipmentFlag[];
}) {
  return (
    <div className="space-y-0.5 lg:space-y-1">
      {/* Title with member dots for shared events */}
      <div className="flex items-center gap-1">
        {showMemberDots && members && (
          <MemberDotsStack members={members} maxVisible={2} size="sm" />
        )}
        {hasEquipmentFlags && <span className="shrink-0" aria-label="Equipment needed">🎒</span>}
        <span className="text-sm lg:text-base font-medium text-text-primary line-clamp-3 break-words">{event.title}</span>
      </div>

      {/* Equipment pills */}
      {hasEquipmentFlags && equipmentFlags && (
        <div className="flex flex-wrap gap-1" data-testid="equipment-pills">
          {equipmentFlags.map((flag) => (
            <span
              key={flag.keyword}
              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-medium"
            >
              {getCategoryEmoji(flag.category)} {flag.item}
            </span>
          ))}
        </div>
      )}

      {/* Member name (alongside color) — shown before time since time is on the axis */}
      {member && (
        <div className="flex items-center gap-1 text-xs lg:text-sm text-text-secondary">
          <div
            className="w-2 h-2 lg:w-3 lg:h-3 rounded-full shrink-0"
            style={{ backgroundColor: member.color }}
          />
          <span className="truncate">{member.name}</span>
        </div>
      )}

      {/* Time + Duration — lower priority since time axis shows this */}
      <div className="flex items-center gap-2 text-xs lg:text-sm text-text-muted">
        <span>{formatTime(event.startTime)}</span>
        <span>({duration})</span>
      </div>

      {/* Location preview */}
      {event.location && (
        <div className="flex items-center gap-1 text-xs lg:text-sm text-text-muted">
          <MapPin className="w-3 h-3 lg:w-4 lg:h-4 shrink-0" />
          <span className="truncate">{event.location}</span>
        </div>
      )}

      {/* Description snippet */}
      {descriptionSnippet && (
        <div className="text-xs lg:text-sm text-text-muted line-clamp-1">{descriptionSnippet}</div>
      )}
    </div>
  );
}
